import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import StepperTabs from './StepperTabs';
import ModalButton from '@/shared/components/ModalButton';
import { ConfigurationFormData } from './formData';

export interface StepDefinition {
  id: string;
  labelId: string;
  fields: (keyof ConfigurationFormData)[];
  render: () => ReactNode;
}

interface Props {
  steps: StepDefinition[];
  unlockAll: boolean;
  disabled: boolean;
  submitLabel: ReactNode;
  openAt?: 'first' | 'last';
  onFinalStepChange?: (onFinalStep: boolean) => void;
  onSubmit: () => void;
}

export default function Stepper({
  steps,
  unlockAll,
  disabled,
  submitLabel,
  openAt = 'first',
  onFinalStepChange,
  onSubmit,
}: Props) {
  const { trigger } = useFormContext<ConfigurationFormData>();
  const [current, setCurrent] = useState(openAt === 'last' ? steps.length - 1 : 0);
  const [visited, setVisited] = useState<string[]>([]);
  const panel = useRef<HTMLDivElement>(null);
  const step = steps[Math.min(current, steps.length - 1)];

  useEffect(() => {
    panel.current?.focus();
  }, [current]);

  const goTo = async (index: number) => {
    if (index > current && !(await trigger(steps[current].fields))) return;

    setVisited((seen) => (seen.includes(step.id) ? seen : [...seen, step.id]));
    setCurrent(index);
  };

  const isLast = current === steps.length - 1;

  useEffect(() => {
    onFinalStepChange?.(isLast);
  }, [isLast, onFinalStepChange]);

  return (
    <div>
      <StepperTabs
        steps={steps}
        current={current}
        selectable={(index) => unlockAll || index <= current || visited.includes(steps[index].id)}
        onSelect={goTo}
      />

      <div
        ref={panel}
        id={`step-panel-${step.id}`}
        role="tabpanel"
        aria-labelledby={`step-tab-${step.id}`}
        tabIndex={-1}
        className="mt-6 focus:outline-none"
      >
        {step.render()}
      </div>

      <div className="mt-6 flex items-center justify-end gap-3 border-t border-slate-200/70 pt-4">
        {current > 0 && (
          <ModalButton type="button" variant="secondary" onClick={() => goTo(current - 1)}>
            <FormattedMessage id="accounts.configForm.back" />
          </ModalButton>
        )}
        <ModalButton
          type="button"
          disabled={disabled}
          onClick={() => (isLast ? onSubmit() : goTo(current + 1))}
        >
          {isLast ? submitLabel : <FormattedMessage id="accounts.configForm.continue" />}
        </ModalButton>
      </div>
    </div>
  );
}
